import {api} from "../api.slice";
import Chat from "@/types/chat";
import Message, {
    AddMessageRequest,
    GetMessageRequestType,
    GetMessagesResponseType,
    MessageStatus,
    ReadMessageRequest,
} from "@/types/message";
import {apiEndpoints} from "@/utils/api-endpoints";
import {synchronizeReadState} from "@/redux/messenger.slice";
import {RootState} from "@/redux/store";

const {Messenger} = apiEndpoints;

export const messageApi = api.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        addMessage: builder.mutation<Message, AddMessageRequest>({
            query: ({hostChatId, file, text, type, repliedOn, sendTime}) => {
                const body = new FormData();
                body.append("sendTime", sendTime.toString());
                if (file) body.append("file", file);
                if (text) body.append("text", text);
                body.append("type", type.toString());
                if (repliedOn) body.append("repliedOn", repliedOn.id.toString());
                return {
                    url: Messenger.AddMessage.url(hostChatId.toString()),
                    method: Messenger.AddMessage.method,
                    body,
                };
            },


        }),
        addAnonymousMessage: builder.mutation<Message, {
            anonymousUserId: string
            hostChatId: string
            file?: File
            text?: string
            type: number
            repliedOn?: { id: string | number }
            sendTime: number
        }>({
            query: ({anonymousUserId, hostChatId, file, text, type, repliedOn, sendTime}) => {
                const body = new FormData();
                body.append("sendTime", sendTime.toString());
                if (file) body.append("file", file);
                if (text) body.append("text", text);
                body.append("type", type.toString());
                if (repliedOn) body.append("repliedOn", repliedOn.id.toString());
                return {
                    url: Messenger.AddAnonymousMessage.url(anonymousUserId, hostChatId),
                    method: Messenger.AddAnonymousMessage.method,
                    body,
                };
            },

        }),

        readMessage: builder.mutation<void, ReadMessageRequest>({
            query: ({messageId, hostChatId}) => ({
                url: Messenger.ReadMessage.url(hostChatId.toString(), messageId.toString()),
                method: Messenger.ReadMessage.method,
            }),

            async onQueryStarted({hostChatId, messageId}, {dispatch, queryFulfilled, getState}) {
                const state = getState() as RootState;
                const pages = state.messenger.pagesToBeLoaded;
                let synchronizedUnreadState: {
                    unreadCount: number;
                    firstUnreadMessageId?: number;
                } | null = null;
                const normalizedHostChatId = String(hostChatId).trim().toUpperCase();
                const selectedChat = state.messenger.selectedChat;
                const selectedChatMatches =
                    String(selectedChat?.id ?? '').trim().toUpperCase() === normalizedHostChatId;
                const normalizedCurrentUserId = String(state.user.currentUser?.id ?? '').trim().toUpperCase();
                const isSelfChat = normalizedHostChatId === normalizedCurrentUserId;
                const localUnreadSource = state.messenger.chatMeta ?? selectedChat;
                const numericMessageId = Number(messageId);

                if (
                    selectedChatMatches &&
                    localUnreadSource?.firstUnreadMessageId != null &&
                    numericMessageId >= Number(localUnreadSource.firstUnreadMessageId)
                ) {
                    const seenCount = numericMessageId - Number(localUnreadSource.firstUnreadMessageId) + 1;
                    const unreadCount = Math.max(0, (localUnreadSource.unreadCount ?? 0) - seenCount);
                    synchronizedUnreadState = {
                        unreadCount,
                        firstUnreadMessageId: unreadCount === 0
                            ? undefined
                            : localUnreadSource.firstUnreadMessageId,
                    };
                }

                const userChatListPatch = dispatch(
                    (api.util.updateQueryData as any)(
                        "getUserChatList",
                        undefined,
                        (draft: any) => {
                            const chat = draft?.find?.(
                                (c: any) => String(c.id).trim().toUpperCase() === normalizedHostChatId
                            );
                            if (chat && synchronizedUnreadState) {
                                chat.unreadCount = synchronizedUnreadState.unreadCount;
                                chat.firstUnreadMessageId = synchronizedUnreadState.firstUnreadMessageId;
                                return;
                            }
                            if (!chat?.firstUnreadMessageId || numericMessageId < Number(chat.firstUnreadMessageId)) return;

                            const seenCount = numericMessageId - Number(chat.firstUnreadMessageId) + 1;
                            chat.unreadCount = Math.max(0, (chat.unreadCount ?? 0) - seenCount);
                            if (chat.unreadCount === 0) chat.firstUnreadMessageId = undefined;
                            synchronizedUnreadState = {
                                unreadCount: chat.unreadCount,
                                firstUnreadMessageId: chat.firstUnreadMessageId,
                            };
                        }
                    )
                );

                const anonymousChatListPatch = dispatch(
                    (api.util.updateQueryData as any)(
                        "getAnonymousChatList",
                        {anonymousUserId: state.anonymousUser?.id ?? ""},
                        (draft: any) => {
                            const chat = draft?.find?.(
                                (c: any) => String(c.id).trim().toUpperCase() === normalizedHostChatId
                            );
                            if (chat && synchronizedUnreadState) {
                                chat.unreadCount = synchronizedUnreadState.unreadCount;
                                chat.firstUnreadMessageId = synchronizedUnreadState.firstUnreadMessageId;
                                return;
                            }
                            if (!chat?.firstUnreadMessageId || numericMessageId < Number(chat.firstUnreadMessageId)) return;

                            const seenCount = numericMessageId - Number(chat.firstUnreadMessageId) + 1;
                            chat.unreadCount = Math.max(0, (chat.unreadCount ?? 0) - seenCount);
                            if (chat.unreadCount === 0) chat.firstUnreadMessageId = undefined;
                            synchronizedUnreadState = {
                                unreadCount: chat.unreadCount,
                                firstUnreadMessageId: chat.firstUnreadMessageId,
                            };
                        }
                    )
                );

                try {
                    await queryFulfilled;
                    const finalUnreadState = synchronizedUnreadState as {
                        unreadCount: number;
                        firstUnreadMessageId?: number;
                    } | null;

                    pages.forEach((pageNumber) => {
                        dispatch(
                            (api.util.updateQueryData as any)(
                                "getMessages",
                                {hostChatId, pageNumber},
                                (draft: GetMessagesResponseType & {value?: GetMessagesResponseType}) => {
                                    const response = draft.value ?? draft;
                                    if (!Array.isArray(response.data)) return;

                                    response.data = response.data.map(message => {
                                        const isIncoming =
                                            isSelfChat ||
                                            String(message.senderId).trim().toUpperCase() !== normalizedCurrentUserId;

                                        return isIncoming && Number(message.id) <= numericMessageId
                                            ? {...message, status: MessageStatus.SEEN}
                                            : message;
                                    });
                                }
                            )
                        );
                    });

                    if (finalUnreadState) {
                        dispatch(
                            (api.util.updateQueryData as any)(
                                "getChatMeta",
                                String(hostChatId),
                                (draft: {unreadCount?: number; firstUnreadMessageId?: number}) => {
                                    draft.unreadCount = finalUnreadState.unreadCount;
                                    draft.firstUnreadMessageId = finalUnreadState.firstUnreadMessageId;
                                }
                            )
                        );
                        dispatch(synchronizeReadState({
                            chatId: String(hostChatId),
                            unreadCount: finalUnreadState.unreadCount,
                            firstUnreadMessageId: finalUnreadState.firstUnreadMessageId,
                        }));
                    }
                } catch {
                    userChatListPatch.undo();
                    anonymousChatListPatch.undo();
                    dispatch(api.util.invalidateTags(["ChatsList"]));
                }
            },
        }),

        editTextMessage: builder.mutation<
            void,
            { messageId: string | number; hostChatId: string | number; text: string }
        >({
            query: ({messageId, hostChatId, text}) => ({
                url: Messenger.EditMessageText.url(
                    hostChatId.toString(),
                    messageId.toString()
                ),
                method: Messenger.EditMessageText.method,
                body: {text},
            }),


        }),

        deleteMessage: builder.mutation<
            void,
            { messageId: string | number; hostChatId: string | number }
        >({
            query: ({messageId}) => ({
                url: Messenger.DeleteMessage.url(messageId.toString()),
                method: Messenger.DeleteMessage.method,
            }),

            async onQueryStarted({messageId, hostChatId}, {dispatch, getState, queryFulfilled}) {
                const state = getState() as RootState;
                const pageNumbers = Array.from(
                    new Set<number>(state.messenger.pagesToBeLoaded)
                );

                const patches = pageNumbers.map((pageNumber) =>
                    dispatch(
                        (api.util.updateQueryData as any)(
                            "getMessages",
                            {hostChatId: hostChatId.toString(), pageNumber},
                            (draft: GetMessagesResponseType & {value?: GetMessagesResponseType}) => {
                                const response = draft.value ?? draft;
                                if (!Array.isArray(response.data)) return;

                                response.data = response.data.filter(
                                    (message) => String(message.id) !== String(messageId)
                                );
                            }
                        )
                    )
                );

                try {
                    await queryFulfilled;
                } catch {
                    patches.forEach((patch) => patch.undo());
                }
            },

        }),
        getUserChatList: builder.query<Chat[], void>({
            query: () => ({
                url: Messenger.GetUserChatList.url,
                method: Messenger.GetUserChatList.method,
            }),
            providesTags: ["ChatsList"],
            serializeQueryArgs: ({endpointName}) => endpointName,
        }),
        getAnonymousChatList: builder.query<Chat[], { anonymousUserId: string }>({
            query: ({anonymousUserId}) => ({
                url: Messenger.GetAnonymousChatList.url(anonymousUserId),
                method: Messenger.GetAnonymousChatList.method,
            }),
            providesTags: ["ChatsList"],
        }),
        getMessages: builder.query<GetMessagesResponseType, GetMessageRequestType>({
            query: ({pageNumber, hostChatId}) => ({
                url: Messenger.GetMessages.url(hostChatId.toString()),
                method: Messenger.GetMessages.method,
                params: {page: pageNumber},
            }),
            providesTags: (result, error, {hostChatId, pageNumber}) => [
                { type: "Messages", id: hostChatId },
            ],
        }),
        getAnonymousMessages: builder.query<GetMessagesResponseType, {
            pageNumber: number;
            hostChatId: string;
            anonymousUserId: string
        }>({
            query: ({pageNumber, hostChatId, anonymousUserId}) => ({
                url: Messenger.GetAnonymousMessages.url(anonymousUserId, hostChatId),
                method: Messenger.GetAnonymousMessages.method,
                params: {page: pageNumber},
            }),
            providesTags: (result, error, {hostChatId, pageNumber}) => [
                {type: "Messages", id: `${hostChatId}-${pageNumber}`},
            ],
        }),
        getMessengerUsers: builder.query<Chat[], void>({
            query: () => ({
                url: Messenger.GetAllMessengerUsers.url,
                method: Messenger.GetAllMessengerUsers.method,
            }),
            providesTags: ["ChatsList"],
        }),
        getChatMeta: builder.query<any, string>({
            query: (hostChatId) => ({
                url: Messenger.GetChatMeta.url(hostChatId),
                method: Messenger.GetChatMeta.method,
            }),
            providesTags: (result, error, hostChatId) => [
                { type: 'ChatMeta', id: hostChatId }
            ],
        }),
        getMessageTargetPage: builder.query<
            { pageNumber: number },
            { chatId: string; messageId: number }
        >({
            query: ({ chatId, messageId }) => ({
                url: Messenger.GetMessageTargetPage.url(chatId, messageId),
                method: Messenger.GetMessageTargetPage.method,
            }),
        }),
    }),
});

export const {
    useAddMessageMutation,
    useAddAnonymousMessageMutation,
    useReadMessageMutation,
    useGetUserChatListQuery,
    useGetAnonymousChatListQuery,
    useGetMessagesQuery,
    useGetAnonymousMessagesQuery,
    useEditTextMessageMutation,
    useDeleteMessageMutation,
    useGetMessengerUsersQuery,
    useGetChatMetaQuery,
    useGetMessageTargetPageQuery
} = messageApi;
